import { Link } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";
import { optimizeGhostImage, formatBlogDate, calculateReadingTime } from "@/lib/blog-utils";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

interface BlogPostCardProps {
  post: {
    slug: string;
    title: string;
    excerpt: string;
    featureImage?: string | null;
    featureImageAlt?: string;
    publishedAt: string;
    readingTime?: number;
    primaryTag?: {
      name: string;
      slug: string;
    } | null;
    primaryAuthor?: {
      name: string;
      slug: string;
      profileImage?: string | null;
    };
  };
  showAuthor?: boolean;
  featured?: boolean;
}

const BlogPostCard = ({ post, showAuthor = true, featured = false }: BlogPostCardProps) => {
  const { getLocalizedPath } = useLocalizedPath();
  const readingTime = calculateReadingTime(post.excerpt, post.readingTime);

  if (featured) {
    return (
      <article className="group bg-card border border-border/50 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300">
        <Link to={getLocalizedPath(`/blog/${post.slug}`)} className="block md:flex">
          <div className="relative md:w-1/2 h-64 md:h-auto overflow-hidden bg-muted">
            {post.featureImage ? (
              <img
                src={optimizeGhostImage(post.featureImage, 800)}
                alt={post.featureImageAlt || post.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
                <span className="text-muted-foreground">No image</span>
              </div>
            )}
            {post.primaryTag && (
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                  {post.primaryTag.name}
                </span>
              </div>
            )}
          </div>

          <div className="md:w-1/2 p-6 md:p-8 flex flex-col justify-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors line-clamp-2">
              {post.title}
            </h2>

            <p className="text-muted-foreground text-base mb-4 line-clamp-3">
              {post.excerpt}
            </p>

            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {showAuthor && post.primaryAuthor && (
                <div className="flex items-center gap-2">
                  {post.primaryAuthor.profileImage ? (
                    <img
                      src={optimizeGhostImage(post.primaryAuthor.profileImage, 48)}
                      alt={post.primaryAuthor.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-xs font-medium text-primary">
                        {post.primaryAuthor.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <span className="font-medium">{post.primaryAuthor.name}</span>
                </div>
              )}
              <span>•</span>
              <span>{formatBlogDate(post.publishedAt)}</span>
              <span>•</span>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{readingTime} min read</span>
              </div>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group bg-card border border-border/50 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 h-full flex flex-col">
      <Link to={getLocalizedPath(`/blog/${post.slug}`)} className="block flex-1 flex flex-col">
        <div className="relative aspect-video overflow-hidden bg-muted">
          {post.featureImage ? (
            <img
              src={optimizeGhostImage(post.featureImage, 600)}
              alt={post.featureImageAlt || post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
              <span className="text-muted-foreground">No image</span>
            </div>
          )}
          {post.primaryTag && (
            <div className="absolute top-4 left-4">
              <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                {post.primaryTag.name}
              </span>
            </div>
          )}
        </div>

        <div className="p-6 flex flex-col flex-1">
          <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
            {post.title}
          </h3>

          <p className="text-muted-foreground text-sm mb-4 leading-5 max-h-[2.5rem] overflow-hidden line-clamp-2">
            {post.excerpt}
          </p>

          <div className="mt-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Clock className="w-4 h-4" />
              <span>{readingTime} min read</span>
            </div>

            <span className="text-primary font-medium text-sm flex items-center gap-1">
              Read more
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
};

export default BlogPostCard;
